import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Copy, Loader2, MailX } from "lucide-react";
import { toast } from "sonner";
import { post } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { AuthLayout } from "@/pages/auth/AuthLayout";
import { Field } from "@/components/common/FormField";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const data = await post("/auth/forgot-password", { email: email.trim() });
      setResult(data);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.debug_reset_token);
      toast.success(t("common.copied"));
    } catch (e) {
      /* clipboard unavailable */
    }
  };

  return (
    <AuthLayout
      title={t("auth.forgotTitle")}
      subtitle={t("auth.forgotSubtitle")}
      footer={<Link to="/login" className="og-focus font-medium text-primary hover:underline" data-testid="forgot-back-to-login">{t("auth.backToLogin")}</Link>}
    >
      {result ? (
        <div className="space-y-4" data-testid="forgot-result">
          <Alert className="border-emerald-200 bg-emerald-50 text-emerald-900"><AlertDescription>{t("auth.forgotSent")}</AlertDescription></Alert>
          {result.debug_reset_token && (
            <Alert className="border-amber-200 bg-amber-50 text-amber-950" data-testid="forgot-debug-token">
              <MailX className="h-4 w-4" />
              <AlertTitle className="text-sm">{t("auth.devTokenNotice")}</AlertTitle>
              <AlertDescription className="mt-2 space-y-3">
                <code className="block break-all rounded-md border border-amber-200 bg-white/70 p-2 font-mono text-xs" data-testid="forgot-debug-token-value">{result.debug_reset_token}</code>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" className="bg-card" onClick={copy} data-testid="forgot-copy-token"><Copy className="mr-1.5 h-3.5 w-3.5" />{t("common.copy")}</Button>
                  <Button asChild size="sm" data-testid="forgot-open-reset"><Link to={`/reset-password?token=${encodeURIComponent(result.debug_reset_token)}`}>{t("auth.openReset")}</Link></Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" noValidate data-testid="forgot-form">
          {error && <Alert className="border-rose-200 bg-rose-50 text-rose-900"><AlertDescription>{error}</AlertDescription></Alert>}
          <Field id="forgot-email" label={t("auth.email")}>
            <Input id="forgot-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("auth.emailPlaceholder")} data-testid="forgot-email-input" />
          </Field>
          <Button type="submit" className="h-10 w-full" disabled={busy || !email} data-testid="forgot-submit-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            {t("auth.sendLink")}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
